import { ErrorComponent as BlitzDefaultErrorComponent } from "@blitzjs/next"
import ErrorLayout from "app/core/layouts/Error"
import Sentry from "integrations/sentry"
import { IntercomProvider } from "react-use-intercom"
import { reportError } from "app/core/utils/error-reporting"
import { Button } from "@superwall/ui"

const DefaultErrorComponent = ({ statusCode }: { statusCode: number }) => {
  // since this is called before the `getLayout` function is prepared, getLayout function will
  // not work here // So we explicitly set the layout

  const getErrorMessage = () => {
    if (statusCode === 404) {
      return {
        heading: "Oops! Page not found.",
        message: "We can't find the page you're looking for. It may have been moved or removed.",
        // action: "Return to the dashboard to continue.",
      }
    } else if (statusCode >= 500) {
      return {
        heading: "Something went wrong.",
        message:
          "We're having trouble loading this page. Our team has been notified and is working on a fix.",
        // action: "Try refreshing the page or return to your dashboard.",
      }
    } else {
      return {
        heading: "Something went wrong.",
        message:
          "We encountered an unexpected issue. Our team has been notified and is looking into this.",
        // action: "Please try again or contact support if the problem persists.",
      }
    }
  }

  const { heading, message } = getErrorMessage()

  return (
    <IntercomProvider appId={"oyf4e3ep"}>
      <ErrorLayout statusCode={statusCode} title={heading}>
        <div className="flex flex-col text-center gap-6 items-center justify-center max-w-lg mx-auto">
          <div className="space-y-2">
            <h1 className="text-8xl font-bold text-gray-200/50">{statusCode}</h1>
            <h2 className="text-3xl text-gray-200 font-semibold">{heading}</h2>
          </div>

          <div className="space-y-3">
            <p className="text-secondary-foreground/80 text-lg">{message}</p>
            {/* <p className="text-secondary-foreground/60">{action}</p> */}
          </div>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            {statusCode != 500 && (
              <Button variant="secondary" onClick={() => (window.location.href = "/dashboard")}>
                Go to Dashboard
              </Button>
            )}
            <Button
              variant={statusCode == 500 ? "secondary" : "default"}
              onClick={() => window.location.reload()}
            >
              Refresh Page
            </Button>
            {statusCode == 500 && (
              <Button
                variant="default"
                onClick={() => (window.location.href = "mailto:support@superwall.com")}
              >
                Contact Support
              </Button>
            )}
          </div>
        </div>
      </ErrorLayout>
    </IntercomProvider>
  )
}

type ExtraProps = {
  hasGetInitialPropsRun?: boolean
  err?: any
}

class ErrorComponent extends BlitzDefaultErrorComponent<ExtraProps> {
  render() {
    const { statusCode, hasGetInitialPropsRun, err } = this.props
    console.log("!! err", err, this.props)
    if (!hasGetInitialPropsRun && err) {
      // getInitialProps is not called in case of
      // https://github.com/vercel/next.js/issues/8592. As a workaround, we pass
      // err via _app.js so it can be captured
      Sentry.captureException(err)

      // Also report to our custom error tracking
      reportError(err, {
        statusCode,
        component: "ErrorComponent",
        hasGetInitialPropsRun,
      })
    }

    return <DefaultErrorComponent statusCode={statusCode} />
  }
}

ErrorComponent.getInitialProps = async (ctx) => {
  const errorInitialProps = await BlitzDefaultErrorComponent.getInitialProps(ctx)

  // Workaround for https://github.com/vercel/next.js/issues/8592, mark when
  // getInitialProps has run
  ;(errorInitialProps as any).hasGetInitialPropsRun = true

  // Running on the server, the response object (`res`) is available.
  // Next.js will pass an err on the server if a page's data fetching methods
  // threw or returned a Promise that rejected
  //
  // Running on the client (browser), Next.js will provide an err if:
  //  - a page's `getInitialProps` threw or returned a Promise that rejected
  //  - an exception was thrown somewhere in the React lifecycle (render,
  //    componentDidMount, etc) that was caught by Next.js's React Error
  //    Boundary. Read more about what types of exceptions are caught by Error
  //    Boundaries: https://reactjs.org/docs/error-boundaries.html

  if (ctx.res?.statusCode === 404) {
    // Opinionated: do not record an exception in Sentry for 404
    return { statusCode: 404 }
  }
  if (ctx.err) {
    Sentry.captureException(ctx.err)

    // Report to our custom error tracking
    await reportError(ctx.err, {
      statusCode: ctx.res?.statusCode || 500,
      path: ctx.asPath,
      isServer: true,
      component: "ErrorComponent.getInitialProps",
    })

    await Sentry.flush(2000)
    return errorInitialProps
  }

  // If this point is reached, getInitialProps was called without any
  // information about what the error might be. This is unexpected and may
  // indicate a bug introduced in Next.js, so record it in Sentry
  Sentry.captureException(
    new Error(`_error.js getInitialProps missing data at path: ${ctx.asPath}`)
  )
  await Sentry.flush(2000)

  return errorInitialProps
}

export default ErrorComponent
